import { useEffect, useState } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { auth } from '@/config/firebase';
import {
  AUTH_TOKEN_COOKIE,
  AUTH_USER_COOKIE,
  setAuthCookie,
  getAuthJsonCookie,
  removeAuthCookie,
} from '@/utils/authCookies';

export interface PublicUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  administrator: boolean;
  entity_id?: string | null;
}

function readStoredUser(): PublicUser | null {
  if (typeof window === 'undefined') return null;
  return getAuthJsonCookie<PublicUser>(AUTH_USER_COOKIE);
}

function buildStoredUser(
  firebaseUser: User,
  previous: PublicUser | null,
): PublicUser {
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName || firebaseUser.email,
    photoURL: firebaseUser.photoURL,
    administrator: true,
    entity_id: previous?.entity_id ?? null,
  };
}

/**
 * Lightweight public-side auth listener.
 *
 * Mirrors the Firebase user into a cookie (D-006) so the public landing header
 * can show the logged-in Google avatar instead of a "Sign In" button.
 */
export function usePublicAuth() {
  // Start signed-out on the first render so the client's initial output matches
  // the prerendered snapshot (which is always rendered signed-out). Reading
  // localStorage synchronously here would make the client render the avatar on
  // first render, diverging from the snapshot and causing a hydration mismatch
  // for signed-in users. Auth state is resolved in the effect below instead.
  const [user, setUser] = useState<PublicUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Resolve from the stored cookie immediately, then confirm against Firebase.
    setUser(readStoredUser());

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Keep the token fresh for any public API calls.
          const token = await firebaseUser.getIdToken();
          setAuthCookie(AUTH_TOKEN_COOKIE, token);

          const stored = buildStoredUser(firebaseUser, readStoredUser());
          setAuthCookie(AUTH_USER_COOKIE, JSON.stringify(stored));
          setUser(stored);
        } catch {
          // If token refresh fails, fall back to whatever is cached.
          setUser(readStoredUser());
        }
      } else {
        removeAuthCookie(AUTH_USER_COOKIE);
        removeAuthCookie(AUTH_TOKEN_COOKIE);
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return { user, loading };
}
